// src/Dashboard/pages/user/Reglements/components/PaymentPlanDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import { addMonths, format } from "date-fns";
import {
  FaCalendarAlt,
  FaCheck,
  FaCoins,
  FaEuroSign,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  createAchatsFractionnes,
  updateReglement,
} from "../../../../../services/achatsServices";

import { sendNotification } from "../../../../../services/notificationServices";

import {
  formatEUR,
  getArticleName,
  toNumberSafe,
} from "../utils/reglementsUtils";

function buildInstallments(totalAmount, count, paymentInterval) {
  const totalCents = Math.round(toNumberSafe(totalAmount) * 100);
  const base = Math.floor(totalCents / count);
  const remainder = totalCents - base * count;

  const today = new Date();

  return Array.from({ length: count }, (_, index) => {
    const cents = index === count - 1 ? base + remainder : base;
    const amount = cents / 100;

    const date = addMonths(
      today,
      index * (Number(paymentInterval) || 1),
    );

    return {
      date,
      amount,
    };
  });
}

export default function PaymentPlanDrawer({
  isOpen,
  onClose,
  totalAmount,
  maxPayments,
  paymentInterval,
  article,
  user,
  onSaved,
}) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [token, setToken] = useState("");

  // Normalisation du nombre maximum de paiements.
  // Si maxPayments est invalide ou inférieur à 1,
  // on considère que seul le paiement en 1 fois est autorisé.
  const normalizedMaxPayments = useMemo(() => {
    const value = Number.parseInt(maxPayments, 10);

    return Number.isFinite(value) && value >= 1 ? value : 1;
  }, [maxPayments]);

  // Le paiement fractionné est autorisé uniquement
  // lorsque l'admin a défini au moins 2 paiements possibles.
  const installmentPaymentsEnabled = normalizedMaxPayments >= 2;

  // Exemples :
  // maxPayments = 1 => []
  // maxPayments = 2 => [2]
  // maxPayments = 3 => [2, 3]
  // maxPayments = 5 => [2, 3, 4, 5]
  const planOptions = useMemo(() => {
    if (!installmentPaymentsEnabled) {
      return [];
    }

    return Array.from(
      { length: normalizedMaxPayments - 1 },
      (_, index) => index + 2,
    );
  }, [normalizedMaxPayments, installmentPaymentsEnabled]);

  useEffect(() => {
    if (!isOpen) return;

    // Sécurité :
    // si max_payments = 1, le drawer ne doit pas pouvoir rester ouvert.
    if (!installmentPaymentsEnabled) {
      onClose?.();
      return;
    }

    setSelectedPlan(null);
    setConfirming(false);

    const storedToken = localStorage.getItem("token");
    setToken(storedToken || "");

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose, installmentPaymentsEnabled]);

  const installments = useMemo(() => {
    if (!selectedPlan) return [];

    // Impossible de créer un échéancier supérieur
    // à ce qui est autorisé dans les settings.
    if (selectedPlan > normalizedMaxPayments) {
      return [];
    }

    return buildInstallments(
      totalAmount,
      selectedPlan,
      paymentInterval,
    );
  }, [
    selectedPlan,
    totalAmount,
    paymentInterval,
    normalizedMaxPayments,
  ]);

  // Si le paiement fractionné n'est pas autorisé,
  // le drawer n'est jamais rendu.
  if (!isOpen || !installmentPaymentsEnabled) {
    return null;
  }

  const handleConfirm = async () => {
    // Sécurité supplémentaire avant toute création d'échéancier.
    if (
      !installmentPaymentsEnabled ||
      !selectedPlan ||
      selectedPlan < 2 ||
      selectedPlan > normalizedMaxPayments ||
      !article ||
      installments.length === 0
    ) {
      return;
    }

    try {
      setConfirming(true);

      const first = installments[0];

      const updatedAchat = {
        user_achat_id: article.user_achat_id,
        article_id: article.article_id,
        quantite: article.quantite,
        prix_total: Number(first.amount),
        date_achat: article.date_achat,
        message_achat: `Échéance 1/${selectedPlan} : Règlement Licence-Cotisation`,
        statut_reglement: article.statut_reglement,
        type_reglement: article.type_reglement,
        type_achat: article.type_achat,
      };

      await updateReglement(
        article.id,
        updatedAchat,
      );

      await createAchatsFractionnes({
        user_achat_id: article.user_achat_id,
        original_achat_id: article.id,
        totalAmount: Number(totalAmount),
        paymentPlan: selectedPlan,
        paymentInterval:
          Number(paymentInterval) >= 1
            ? Number(paymentInterval)
            : 1,
      });

      for (
        let index = 1;
        index < installments.length;
        index += 1
      ) {
        const installment = installments[index];

        const message = `Merci de penser à régler l'échéance n°${
          index + 1
        } (${formatEUR(installment.amount)}) prévue le ${format(
          installment.date,
          "dd/MM/yyyy",
        )}.`;

        await sendNotification(
          message,
          article.user_achat_id,
          token,
        );
      }

      toast.success("Échéancier créé avec succès.");

      await onSaved?.();
    } catch (error) {
      console.error(
        "Erreur fractionnement règlement :",
        error,
      );

      toast.error(
        "Erreur lors du fractionnement du règlement.",
      );
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              <FaCoins />
              Paiement fractionné
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              Règlement en plusieurs fois
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              Article concerné
            </p>

            <h3 className="mt-2 text-lg font-bold text-slate-900 dark:text-white">
              {getArticleName(article)}
            </h3>

            <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl bg-white p-4 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Montant total
                </p>

                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {formatEUR(totalAmount)}
                </p>
              </div>

              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#013E8E] text-white">
                <FaEuroSign />
              </div>
            </div>
          </div>

          <div className="mt-5">
            <p className="text-sm font-bold text-slate-900 dark:text-white">
              Choisissez le nombre d’échéances
            </p>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Intervalle entre échéances :{" "}
              {Number(paymentInterval) || 1} mois.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {planOptions.map((plan) => {
                const active = selectedPlan === plan;

                return (
                  <button
                    key={plan}
                    type="button"
                    onClick={() => setSelectedPlan(plan)}
                    disabled={confirming}
                    className={`rounded-[22px] border px-4 py-4 text-center transition ${
                      active
                        ? "border-[#013E8E] bg-[#013E8E] text-white shadow-md"
                        : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
                    }`}
                  >
                    <span className="block text-2xl font-bold">
                      x{plan}
                    </span>

                    <span className="mt-1 block text-xs font-bold opacity-80">
                      échéances
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {installments.length > 0 && (
            <div className="mt-6 overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  Échéancier prévisionnel
                </p>
              </div>

              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {installments.map((installment, index) => (
                  <div
                    key={`${installment.date}-${index}`}
                    className="flex items-center justify-between gap-4 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                          index === 0
                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                            : "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                        }`}
                      >
                        {index + 1}
                      </span>

                      <div>
                        <p className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                          <FaCalendarAlt className="text-slate-400" />

                          {format(
                            installment.date,
                            "dd/MM/yyyy",
                          )}
                        </p>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Échéance {index + 1}/{selectedPlan}
                          {index === 0
                            ? " — aujourd’hui"
                            : ""}
                        </p>
                      </div>
                    </div>

                    <p className="text-sm font-bold text-slate-900 dark:text-white">
                      {formatEUR(installment.amount)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 flex items-start gap-3 rounded-[24px] border border-indigo-200 bg-indigo-50 p-4 text-sm leading-6 text-indigo-800 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-200">
            <FaInfoCircle className="mt-1 shrink-0" />

            <p>
              Le premier paiement est positionné immédiatement.
              Les échéances suivantes sont créées automatiquement
              et des notifications de rappel sont envoyées aux
              dates prévues.
            </p>
          </div>
        </div>

        <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={confirming}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={
                !selectedPlan ||
                selectedPlan > normalizedMaxPayments ||
                confirming
              }
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaCheck />

              {confirming
                ? "Traitement..."
                : "Confirmer l’échéancier"}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}